document.documentElement.classList.remove('no-js');

const formatStoreMoney = (cents, currency = 'PEN') => {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'es-PE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  } catch (_error) {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
};

const getVariantId = (productRoot) => {
  const select = productRoot?.querySelector('[data-variant-select]');
  const input = productRoot?.querySelector('[data-variant-input]');
  return select?.value || input?.value || '';
};

const updatePurchaseOptions = (productRoot) => {
  if (!productRoot) return;

  const offers = [...productRoot.querySelectorAll('[data-quantity-offer]')];
  if (!offers.length) return;

  const unitPrice = Number.parseInt(productRoot.dataset.priceCents || '0', 10);
  const currency = productRoot.dataset.currency || 'PEN';
  const stock = productRoot.querySelector('[data-stock-status]');
  const inventoryTracked = stock?.dataset.inventoryTracked === 'true';
  const inventory = Number.parseInt(stock?.dataset.inventoryQuantity || '0', 10);
  const variantAvailable = productRoot.dataset.variantAvailable === 'true';

  offers.forEach((offer) => {
    const quantity = Number.parseInt(offer.dataset.quantity || '1', 10);
    const discountPercent = Number.parseInt(offer.dataset.discountPercent || '0', 10);
    const discountCode = (offer.dataset.discountCode || '').trim();
    const hasRealDiscount = Boolean(discountCode) && discountPercent > 0;
    const originalTotal = unitPrice * quantity;
    const finalTotal = hasRealDiscount
      ? Math.round(originalTotal * (100 - discountPercent) / 100)
      : originalTotal;
    const available = variantAvailable && (!inventoryTracked || inventory <= 0 || quantity <= inventory);

    offer.disabled = !available;
    offer.dataset.totalCents = String(finalTotal);
    const price = offer.querySelector('[data-offer-price]');
    const compare = offer.querySelector('[data-offer-compare]');
    const saving = offer.querySelector('[data-offer-saving]');
    if (price) price.textContent = formatStoreMoney(finalTotal, currency);
    if (compare) {
      compare.textContent = hasRealDiscount ? formatStoreMoney(originalTotal, currency) : '';
      compare.hidden = !hasRealDiscount;
    }
    if (saving) {
      saving.textContent = hasRealDiscount ? `Ahorras ${discountPercent}%` : '';
      saving.hidden = !hasRealDiscount;
    }
  });

  let selected = offers.find((offer) => offer.classList.contains('is-selected') && !offer.disabled);
  if (!selected) selected = offers.find((offer) => !offer.disabled);
  if (!selected) return;

  offers.forEach((offer) => {
    const isSelected = offer === selected;
    offer.classList.toggle('is-selected', isSelected);
    offer.setAttribute('aria-checked', String(isSelected));
  });

  const quantity = Number.parseInt(selected.dataset.quantity || '1', 10);
  const quantityInput = productRoot.querySelector('[data-quantity-input]');
  if (quantityInput) quantityInput.value = String(quantity);
  productRoot.dataset.selectedDiscountCode = (selected.dataset.discountCode || '').trim();
  productRoot.dataset.selectedQuantity = String(quantity);
  productRoot.querySelectorAll('[data-sticky-quantity]').forEach((node) => {
    node.textContent = `${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}`;
  });
  productRoot.querySelectorAll('[data-sticky-price]').forEach((node) => {
    node.textContent = formatStoreMoney(Number.parseInt(selected.dataset.totalCents || '0', 10), currency);
  });
};

const startDirectCheckout = (productRoot, extraDiscountCode = '') => {
  if (!productRoot || productRoot.dataset.variantAvailable !== 'true') return;

  const variantId = getVariantId(productRoot);
  const quantityInput = productRoot.querySelector('[data-quantity-input]');
  const quantity = Math.max(1, Number.parseInt(quantityInput?.value || productRoot.dataset.selectedQuantity || '1', 10));
  if (!variantId) return;

  const discountCodes = [productRoot.dataset.selectedDiscountCode, extraDiscountCode]
    .map((code) => (code || '').trim())
    .filter((code, index, codes) => code && codes.indexOf(code) === index);
  const cartUrl = (productRoot.dataset.cartUrl || '/cart').replace(/\/$/, '');
  const query = [`ref=${encodeURIComponent('peru-bacan-product')}`];
  if (discountCodes.length) query.unshift(`discount=${discountCodes.map(encodeURIComponent).join(',')}`);

  productRoot.querySelectorAll('[data-add-button], [data-sticky-add]').forEach((button) => {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  });
  window.location.assign(`${cartUrl}/${encodeURIComponent(variantId)}:${quantity}?${query.join('&')}`);
};

const updateProductButtonState = (productRoot) => {
  const buttons = productRoot?.querySelectorAll('[data-add-button], [data-sticky-add]');
  if (!buttons?.length) return;

  const variantAvailable = productRoot.dataset.variantAvailable === 'true';
  const coverageRequired = productRoot.dataset.coverageRequired === 'true';
  const coverageComplete = productRoot.dataset.coverageComplete === 'true';
  buttons.forEach((button) => {
    button.disabled = !variantAvailable || (coverageRequired && !coverageComplete);
  });
};

const updateProductVariantUI = (productRoot, option) => {
  if (!productRoot || !option) return;

  const available = option.dataset.available === 'true';
  const tracked = option.dataset.inventoryTracked === 'true';
  const inventory = Number.parseInt(option.dataset.inventoryQuantity || '0', 10);
  const threshold = Number.parseInt(productRoot.dataset.lowStockThreshold || '0', 10);
  const exactStock = productRoot.dataset.showExactStock === 'true';
  const lowStock = available && tracked && threshold > 0 && inventory > 0 && inventory <= threshold;
  const price = option.dataset.price || '';
  const comparePrice = option.dataset.comparePrice || '';
  const priceCents = Number.parseInt(option.dataset.priceCents || '0', 10);
  const compareCents = Number.parseInt(option.dataset.compareCents || '0', 10);

  productRoot.dataset.variantAvailable = String(available);
  productRoot.dataset.priceCents = String(priceCents);
  updateProductButtonState(productRoot);

  productRoot.querySelectorAll('[data-add-label]').forEach((label) => {
    label.textContent = available ? label.dataset.availableLabel : label.dataset.soldOutLabel;
  });
  productRoot.querySelectorAll('[data-add-subline]').forEach((line) => {
    line.textContent = available ? 'Completa tus datos y paga al recibir' : 'Prueba con otra opción';
  });
  productRoot.querySelectorAll('[data-sticky-label]').forEach((label) => {
    const mainLabel = productRoot.querySelector('[data-add-label]');
    label.textContent = available ? mainLabel?.dataset.availableLabel || 'Comprar ahora' : mainLabel?.dataset.soldOutLabel || 'Agotado';
  });

  productRoot.querySelectorAll('[data-price-current]').forEach((node) => { node.textContent = price; });
  productRoot.querySelectorAll('[data-price-compare]').forEach((node) => {
    node.textContent = comparePrice;
    node.hidden = !(compareCents > priceCents && comparePrice);
  });
  productRoot.querySelectorAll('[data-price-saving]').forEach((node) => {
    const hasSaving = compareCents > priceCents && compareCents > 0;
    node.textContent = hasSaving ? `Ahorras ${Math.round(((compareCents - priceCents) / compareCents) * 100)}%` : '';
    node.hidden = !hasSaving;
  });
  productRoot.querySelectorAll('[data-sticky-price]').forEach((node) => { node.textContent = price; });

  const stock = productRoot.querySelector('[data-stock-status]');
  const stockTitle = stock?.querySelector('[data-stock-title]');
  const stockCopy = stock?.querySelector('[data-stock-copy]');

  if (stock) {
    stock.dataset.available = String(available);
    stock.dataset.inventoryTracked = String(tracked);
    stock.dataset.inventoryQuantity = String(inventory);
    stock.classList.toggle('pb-stock-status--low', lowStock);
    stock.classList.toggle('pb-stock-status--available', available && !lowStock);
    stock.classList.toggle('pb-stock-status--sold-out', !available);
  }

  if (stockTitle && stockCopy) {
    if (!available) {
      stockTitle.textContent = 'Agotado por ahora';
      stockCopy.textContent = 'Elige otra variante disponible.';
    } else if (lowStock) {
      stockTitle.textContent = exactStock ? `¡Últimas ${inventory} unidades disponibles!` : '¡Quedan pocas unidades!';
      stockCopy.textContent = 'Si ya lo elegiste, conviene pedirlo antes de que se agote.';
    } else {
      stockTitle.textContent = 'Disponible para pedir';
      stockCopy.textContent = 'Confirmamos existencia y zona antes del despacho.';
    }
  }

  productRoot.querySelectorAll('[data-stock-badge]').forEach((badge) => { badge.hidden = !lowStock; });

  const quantityInput = productRoot.querySelector('[data-quantity-input]');
  if (quantityInput) {
    if (available && tracked && inventory > 0) quantityInput.max = String(inventory);
    else quantityInput.removeAttribute('max');
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updatePurchaseOptions(productRoot);
};

const menuButton = document.querySelector('[data-menu-toggle]');
const menu = document.querySelector('[data-mobile-menu]');

if (menuButton && menu) {
  menuButton.addEventListener('click', () => {
    const isOpen = menu.getAttribute('data-open') === 'true';
    menu.setAttribute('data-open', String(!isOpen));
    menuButton.setAttribute('aria-expanded', String(!isOpen));
    document.body.classList.toggle('pb-menu-open', !isOpen);
  });
}

document.querySelectorAll('[data-variant-select]').forEach((select) => {
  select.addEventListener('change', (event) => {
    const option = event.target.selectedOptions[0];
    const productRoot = event.target.closest('[data-product-root]');
    updateProductVariantUI(productRoot, option);

    if (option?.dataset.url && window.history?.replaceState) {
      window.history.replaceState({}, '', option.dataset.url);
    }
  });
});

document.querySelectorAll('[data-coverage-checker]').forEach((checker) => {
  const productRoot = checker.closest('[data-product-root]');
  const departmentSelect = checker.querySelector('[data-department-select]');
  const districtSelect = checker.querySelector('[data-district-select]');
  const message = checker.querySelector('[data-coverage-message]');
  const rulesElement = checker.querySelector('[data-coverage-rules]');

  if (!productRoot || !departmentSelect || !districtSelect || !message || !rulesElement) return;

  let rawRules = '';
  try {
    rawRules = JSON.parse(rulesElement.textContent || '""');
  } catch (_error) {
    rawRules = '';
  }

  const coverage = new Map();
  rawRules.split(/\r?\n/).forEach((line) => {
    const separator = line.indexOf('|');
    if (separator < 1) return;

    const department = line.slice(0, separator).trim();
    const districts = line
      .slice(separator + 1)
      .split(',')
      .map((district) => district.trim())
      .filter(Boolean);

    if (department && districts.length) coverage.set(department, [...new Set(districts)]);
  });

  coverage.forEach((_districts, department) => {
    departmentSelect.add(new Option(department, department));
  });

  const setCoverageState = (complete) => {
    productRoot.dataset.coverageComplete = String(complete);
    message.classList.toggle('pb-coverage__message--ready', complete);
    message.textContent = complete ? message.dataset.readyMessage : message.dataset.emptyMessage;
    updateProductButtonState(productRoot);
  };

  departmentSelect.addEventListener('change', () => {
    const districts = coverage.get(departmentSelect.value) || [];
    districtSelect.replaceChildren(new Option(
      districts.length ? 'Selecciona tu distrito' : 'Zona todavía no habilitada',
      ''
    ));
    districts.forEach((district) => districtSelect.add(new Option(district, district)));
    districtSelect.disabled = districts.length === 0;
    setCoverageState(false);
  });

  districtSelect.addEventListener('change', () => setCoverageState(Boolean(districtSelect.value)));
  setCoverageState(false);
});

document.querySelectorAll('[data-product-root]').forEach(updateProductButtonState);

document.querySelectorAll('[data-purchase-options]').forEach((options) => {
  const productRoot = options.closest('[data-product-root]');
  if (!productRoot) return;

  options.querySelectorAll('[data-quantity-offer]').forEach((offer) => {
    offer.addEventListener('click', () => {
      if (offer.disabled) return;
      options.querySelectorAll('[data-quantity-offer]').forEach((candidate) => {
        const isSelected = candidate === offer;
        candidate.classList.toggle('is-selected', isSelected);
        candidate.setAttribute('aria-checked', String(isSelected));
      });
      updatePurchaseOptions(productRoot);
    });
  });

  updatePurchaseOptions(productRoot);
});

document.querySelectorAll('[data-quantity-control]').forEach((control) => {
  const productRoot = control.closest('[data-product-root]');
  const input = control.querySelector('[data-quantity-input]');
  const minus = control.querySelector('[data-quantity-minus]');
  const plus = control.querySelector('[data-quantity-plus]');
  if (!productRoot || !input) return;

  const updateQuantity = (nextValue) => {
    const minimum = Number.parseInt(input.min || '1', 10);
    const maximum = Number.parseInt(input.max || '0', 10);
    let quantity = Math.max(minimum, Number.parseInt(String(nextValue), 10) || minimum);
    if (maximum > 0) quantity = Math.min(quantity, maximum);
    input.value = String(quantity);
    if (minus) minus.disabled = quantity <= minimum;
    if (plus) plus.disabled = maximum > 0 && quantity >= maximum;
    productRoot.querySelectorAll('[data-sticky-quantity]').forEach((node) => {
      node.textContent = `${quantity} ${quantity === 1 ? 'unidad' : 'unidades'}`;
    });
    if (!productRoot.querySelector('[data-purchase-options]')) {
      const total = Number.parseInt(productRoot.dataset.priceCents || '0', 10) * quantity;
      productRoot.querySelectorAll('[data-sticky-price]').forEach((node) => {
        node.textContent = formatStoreMoney(total, productRoot.dataset.currency || 'PEN');
      });
    }
  };

  minus?.addEventListener('click', () => updateQuantity(Number(input.value) - 1));
  plus?.addEventListener('click', () => updateQuantity(Number(input.value) + 1));
  input.addEventListener('input', () => updateQuantity(input.value));
  updateQuantity(input.value);
});

document.querySelectorAll('.pb-product-form').forEach((form) => {
  const productRoot = form.closest('[data-product-root]');
  if (!productRoot || productRoot.dataset.directCheckout !== 'true') return;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    startDirectCheckout(productRoot);
  });
});

document.querySelectorAll('[data-mobile-purchase-bar]').forEach((bar) => {
  const productRoot = bar.closest('[data-product-root]');
  const stickyButton = bar.querySelector('[data-sticky-add]');
  const mainButton = productRoot?.querySelector('[data-add-button]');
  stickyButton?.addEventListener('click', () => {
    if (!stickyButton.disabled) mainButton?.click();
  });
});

document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
  const track = gallery.querySelector('[data-product-gallery-track]');
  const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
  const thumbs = [...gallery.querySelectorAll('[data-gallery-target]')];
  const current = gallery.querySelector('[data-gallery-current]');
  if (!track || slides.length < 2) return;

  const setCurrent = (index) => {
    if (current) current.textContent = String(index + 1);
    thumbs.forEach((thumb, thumbIndex) => {
      if (thumbIndex === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  };

  thumbs.forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const index = Number.parseInt(thumb.dataset.galleryTarget || '0', 10);
      track.scrollTo({ left: slides[index]?.offsetLeft || 0, behavior: 'smooth' });
      setCurrent(index);
    });
  });

  let frame;
  track.addEventListener('scroll', () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = window.requestAnimationFrame(() => {
      const index = slides.reduce((closest, slide, slideIndex) => (
        Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[closest].offsetLeft - track.scrollLeft) ? slideIndex : closest
      ), 0);
      setCurrent(index);
    });
  }, { passive: true });
});

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

document.querySelectorAll('[data-motion-banner]').forEach((banner) => {
  const toggle = banner.querySelector('[data-motion-toggle]');
  const pauseLabel = toggle?.querySelector('[data-motion-pause]');
  const playLabel = toggle?.querySelector('[data-motion-play]');

  const setPaused = (paused) => {
    banner.dataset.paused = String(paused);
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(paused));
    toggle.setAttribute('aria-label', paused ? 'Reproducir mensajes en movimiento' : 'Pausar mensajes en movimiento');
    if (pauseLabel) pauseLabel.hidden = paused;
    if (playLabel) playLabel.hidden = !paused;
  };

  setPaused(prefersReducedMotion.matches);
  toggle?.addEventListener('click', () => setPaused(banner.dataset.paused !== 'true'));
});

document.querySelectorAll('[data-reviews-viewport]').forEach((viewport) => {
  const section = viewport.closest('.pb-reviews');
  const nextButton = section?.querySelector('[data-review-next]');
  const prevButton = section?.querySelector('[data-review-prev]');
  const toggleButton = section?.querySelector('[data-review-toggle]');
  const card = viewport.querySelector('.pb-review');
  const autoplayEnabled = section?.dataset.reviewAutoplay === 'true';
  let timer;

  const step = () => (card?.getBoundingClientRect().width || 360) + 18;
  const move = (direction) => viewport.scrollBy({ left: direction * step(), behavior: 'smooth' });
  const stopAutoplay = () => {
    if (timer) window.clearInterval(timer);
    timer = undefined;
  };
  const startAutoplay = () => {
    if (!autoplayEnabled || prefersReducedMotion.matches || timer) return;
    timer = window.setInterval(() => {
      const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 12;
      viewport.scrollTo({ left: atEnd ? 0 : viewport.scrollLeft + step(), behavior: 'smooth' });
    }, 5200);
  };
  const setAutoplayPaused = (paused) => {
    if (paused) stopAutoplay();
    else startAutoplay();
    if (!toggleButton) return;
    toggleButton.setAttribute('aria-pressed', String(paused));
    toggleButton.setAttribute('aria-label', paused ? 'Reproducir reseñas' : 'Pausar reseñas');
    toggleButton.textContent = paused ? '▶' : 'Ⅱ';
  };

  nextButton?.addEventListener('click', () => move(1));
  prevButton?.addEventListener('click', () => move(-1));
  toggleButton?.addEventListener('click', () => setAutoplayPaused(toggleButton.getAttribute('aria-pressed') !== 'true'));
  viewport.addEventListener('mouseenter', stopAutoplay);
  viewport.addEventListener('mouseleave', startAutoplay);
  viewport.addEventListener('focusin', stopAutoplay);
  viewport.addEventListener('focusout', startAutoplay);

  setAutoplayPaused(prefersReducedMotion.matches);
});
